import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pricesAreStale, pricesLastReviewed, type AppSettings, type PriceFeedModel, type TokenPrices } from '@ace-tracker/shared';
import { applyPriceFeed, fetchPriceFeedStatus } from '../../api';
import { SETTINGS_QUERY_KEY } from '../../settings';
import { Button } from '../Button';
import { Card } from './formParts';

function rate(value: number | undefined): string {
  return value === undefined ? '—' : `$${value.toLocaleString(undefined, { maximumFractionDigits: 4 })}`;
}

function PriceCell({ prices }: { prices: TokenPrices | null | undefined }) {
  if (!prices) return <span className="text-text-faint">Fallback rate</span>;
  return (
    <span className="tabular-nums">
      {rate(prices.inputPerMillion)} / {rate(prices.outputPerMillion)}
      {prices.cacheReadPerMillion !== undefined && <span className="text-text-faint"> · read {rate(prices.cacheReadPerMillion)}</span>}
    </span>
  );
}

const PRICE_FIELDS: [keyof TokenPrices, string][] = [
  ['inputPerMillion', 'input'],
  ['outputPerMillion', 'output'],
  ['cacheReadPerMillion', 'cache read'],
  ['cacheWritePerMillion', 'cache write'],
];

/** Which current prices differ from the list price, or that only the earlier price history does. */
function difference(m: PriceFeedModel): string {
  const current = m.history[m.history.length - 1];
  if (!m.applied) return 'Not priced yet';
  if (!current) return '—';
  const fields = PRICE_FIELDS.filter(([key]) => m.applied?.[key] !== current[key]).map(([key, label]) => `${label} ${rate(current[key])}`);
  return fields.length > 0 ? fields.join(', ') : 'Earlier prices only';
}

/** the pricing rows with the public list-price
 * feed and applies its dated history on request (E2.2), and shows when prices
 * were last reviewed (E1.11).
 */
export function PriceFeedPanel({ settings, unpricedModels, dirty }: { settings: AppSettings; unpricedModels: string[]; dirty: boolean }) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<string>();
  const queryKey = ['price-feed', unpricedModels];
  const status = useQuery({ queryKey, queryFn: () => fetchPriceFeedStatus(unpricedModels), staleTime: 60_000 });

  const refresh = useMutation({
    mutationFn: () => fetchPriceFeedStatus(unpricedModels, true),
    onSuccess: (data) => queryClient.setQueryData(queryKey, data),
  });
  const apply = useMutation({
    mutationFn: (models: string[]) => applyPriceFeed(models),
    onSuccess: ({ settings: saved, updated }) => {
      queryClient.setQueryData(SETTINGS_QUERY_KEY, saved);
      for (const key of ['price-feed', 'dashboard-summary', 'dashboard-overview', 'dashboard-executive', 'dashboard-health', 'dashboard-spend', 'dashboard-efficiency', 'dashboard-sessions', 'dashboard-budget', 'settings-history']) {
        void queryClient.invalidateQueries({ queryKey: [key] });
      }
      setNotice(updated.length > 0 ? `Updated ${updated.length} model${updated.length === 1 ? '' : 's'} from the price feed.` : 'Already up to date.');
    },
  });

  const reviewed = pricesLastReviewed(settings);
  const stale = pricesAreStale(settings);
  const data = status.data;
  const differing: PriceFeedModel[] = (data?.models ?? []).filter((m) => m.feedModelId && m.differs);
  const notInFeed = (data?.models ?? []).filter((m) => !m.feedModelId).length;
  const busy = apply.isPending || refresh.isPending;
  const error = (apply.error ?? refresh.error ?? status.error) as Error | null;
  const canApply = !!data?.applicable && !!data.fetchedAt && !dirty && !busy;

  return (
    <Card
      title="List prices and review"
      description={
        <>
          Dashboard cost is <b className="font-medium">indicative</b>: tokens × the rows below, not an invoice. Compare them with
          public list prices from <span className="font-mono">pydantic/genai-prices</span> (MIT). Applying keeps the feed's
          price history as dated rows, so past days are costed at the price in force then.
        </>
      }
    >
      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-text-muted" aria-live="polite">
        <p>
          <i aria-hidden="true" className={`fa-solid ${stale ? 'fa-triangle-exclamation text-warn-ink' : 'fa-calendar-check text-success'} mr-1.5`} />
          Prices last reviewed <b className="font-medium text-text-default">{new Date(reviewed).toLocaleDateString()}</b>
          {stale && ' — more than 90 days ago; check them against current list or contract prices.'}
        </p>
        {data && !data.enabled && <p>Price feed is turned off (PRICE_FEED_ENABLED=false).</p>}
        {data?.enabled && (
          <p>
            Feed {data.fetchedAt ? <>checked {new Date(data.fetchedAt).toLocaleString()}</> : 'not downloaded yet'}
            {data.autoApply && ' · applied automatically (Cost tab)'}
          </p>
        )}
      </div>

      {data?.error && (
        <p className="mt-2 text-xs text-error">
          <i aria-hidden="true" className="fa-solid fa-circle-exclamation mr-1.5" />
          Last download failed: {data.error}
        </p>
      )}
      {data && !data.applicable && (
        <p className="mt-2 text-xs text-text-muted">The feed is in USD; it can't be applied while the Cost tab uses another currency.</p>
      )}
      {error && <p className="mt-2 text-xs text-error">{error.message}</p>}
      {notice && !error && <p className="mt-2 text-xs text-text-muted">{notice}</p>}

      {data?.enabled && (
        <>
          <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[880px] text-left text-sm">
              <caption className="sr-only">Models whose prices differ from the list-price feed</caption>
              <thead className="border-b border-border text-[11px] uppercase tracking-wide text-text-faint">
                <tr>
                  <th className="pb-2.5 pr-2">Model id</th>
                  <th className="pb-2.5 pr-2">Applied today (in / out)</th>
                  <th className="pb-2.5 pr-2">List price (in / out)</th>
                  <th className="pb-2.5 pr-2">List since</th>
                  <th className="pb-2.5 pr-2">What differs</th>
                  <th className="pb-2.5">
                    <span className="sr-only">Apply</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {differing.map((m) => {
                  const current = m.history[m.history.length - 1];
                  return (
                    <tr key={m.model} className="border-b border-border last:border-0">
                      <td className="py-2 pr-2 font-mono text-xs">
                        {m.model}
                        {m.feedModelId !== m.model && <span className="ml-1 text-text-faint">({m.feedModelId})</span>}
                      </td>
                      <td className="py-2 pr-2 text-xs">
                        <PriceCell prices={m.applied} />
                      </td>
                      <td className="py-2 pr-2 text-xs">
                        <PriceCell prices={current} />
                        {m.history.length > 1 && <span className="ml-1 text-text-faint">· {m.history.length} price periods</span>}
                      </td>
                      <td className="py-2 pr-2 text-xs tabular-nums">{current?.effectiveFrom ?? '—'}</td>
                      <td className="py-2 pr-2 text-xs text-text-muted">{difference(m)}</td>
                      <td className="py-2 text-right">
                        <Button type="button" variant="text" disabled={!canApply} onClick={() => apply.mutate([m.model])}>
                          Apply
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {data.fetchedAt && differing.length === 0 && (
                  <tr>
                    <td colSpan={6} className="py-3 text-text-muted">
                      Every model the feed lists matches its list prices.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button type="button" variant="secondary" disabled={busy} onClick={() => refresh.mutate()}>
              <i aria-hidden="true" className={`fa-solid fa-rotate ${refresh.isPending ? 'fa-spin' : ''}`} />
              {refresh.isPending ? 'Checking…' : 'Check now'}
            </Button>
            <Button type="button" disabled={!canApply || differing.length === 0} onClick={() => apply.mutate(differing.map((m) => m.model))}>
              Apply list prices to {differing.length} model{differing.length === 1 ? '' : 's'}
            </Button>
            <p className="text-xs text-text-faint">
              {dirty ? 'Save or discard your edits first. ' : ''}
              {notInFeed > 0 && `${notInFeed} model${notInFeed === 1 ? ' is' : 's are'} not in the feed — price them by hand.`}
            </p>
          </div>
        </>
      )}
    </Card>
  );
}
